import './cta-blokken.scss';

// Slider port of the legacy UIkit 2 data-uk-slider behaviour, extended with
// optional autoplay. Re-uses the existing markup (uk-slider-container /
// ul.uk-slider / [data-uk-slider-item="previous|next"]) so the dumped legacy
// CSS keeps working. Moves the track in whole-slide steps; supports pointer
// swipe and loops back to the start when autoplay hits the end.

const SWIPE_THRESHOLD_RATIO = 0.2;
const DEFAULT_AUTOPLAY_INTERVAL = 3000;

const initSlider = ( root ) => {
	const container = root.querySelector( '.uk-slider-container' );
	const track = root.querySelector( '.uk-slider' );
	const prev = root.querySelector( '[data-uk-slider-item="previous"]' );
	const next = root.querySelector( '[data-uk-slider-item="next"]' );

	if ( ! container || ! track ) {
		return;
	}

	const items = Array.from( track.children );
	if ( items.length === 0 ) {
		return;
	}

	const autoplayEnabled = root.getAttribute( 'data-slider-autoplay' ) === 'true';
	const autoplayInterval = Math.max(
		1000,
		parseInt( root.getAttribute( 'data-slider-autoplay-interval' ), 10 ) || DEFAULT_AUTOPLAY_INTERVAL
	);

	let index = 0;
	let pointerStartX = null;
	let pointerStartOffset = 0;
	let activePointerId = null;
	let autoplayTimer = null;

	const slideWidth = () => items[ 0 ].getBoundingClientRect().width;

	const visibleCount = () => {
		const width = slideWidth();
		if ( width <= 0 ) {
			return 1;
		}
		return Math.max( 1, Math.round( container.clientWidth / width ) );
	};

	const maxIndex = () => Math.max( 0, items.length - visibleCount() );

	const clamp = ( value ) => Math.min( Math.max( value, 0 ), maxIndex() );

	const updateNav = () => {
		const atStart = index <= 0;
		const atEnd = index >= maxIndex();
		if ( prev ) {
			prev.classList.toggle( 'uk-invisible', atStart && ! autoplayEnabled );
			prev.setAttribute( 'aria-disabled', atStart && ! autoplayEnabled ? 'true' : 'false' );
		}
		if ( next ) {
			next.classList.toggle( 'uk-invisible', atEnd && ! autoplayEnabled );
			next.setAttribute( 'aria-disabled', atEnd && ! autoplayEnabled ? 'true' : 'false' );
		}
	};

	const apply = ( animate = true ) => {
		index = clamp( index );
		track.style.transition = animate ? '' : 'none';
		track.style.transform = `translate3d(${ -index * slideWidth() }px, 0, 0)`;
		updateNav();
	};

	const go = ( delta ) => {
		if ( autoplayEnabled && items.length > visibleCount() ) {
			// With autoplay on, wrap around instead of stopping at the edges.
			const total = maxIndex() + 1;
			index = ( index + delta + total ) % total;
		} else {
			index = clamp( index + delta );
		}
		apply( true );
	};

	const stopAutoplay = () => {
		if ( autoplayTimer !== null ) {
			window.clearInterval( autoplayTimer );
			autoplayTimer = null;
		}
	};

	const startAutoplay = () => {
		if ( ! autoplayEnabled ) {
			return;
		}
		if ( items.length <= visibleCount() ) {
			return;
		}
		stopAutoplay();
		autoplayTimer = window.setInterval( () => go( 1 ), autoplayInterval );
	};

	if ( prev ) {
		prev.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			go( -1 );
			startAutoplay();
		} );
	}

	if ( next ) {
		next.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			go( 1 );
			startAutoplay();
		} );
	}

	// Pause autoplay while the user hovers, restart when they leave.
	root.addEventListener( 'mouseenter', stopAutoplay );
	root.addEventListener( 'mouseleave', startAutoplay );
	root.addEventListener( 'focusin', stopAutoplay );
	root.addEventListener( 'focusout', startAutoplay );

	// Stop autoplay when the tab is hidden so it doesn't keep firing.
	document.addEventListener( 'visibilitychange', () => {
		if ( document.hidden ) {
			stopAutoplay();
		} else {
			startAutoplay();
		}
	} );

	const ro = new ResizeObserver( () => apply( false ) );
	ro.observe( container );

	container.addEventListener( 'pointerdown', ( event ) => {
		if ( event.pointerType === 'mouse' && event.button !== 0 ) {
			return;
		}
		pointerStartX = event.clientX;
		pointerStartOffset = -index * slideWidth();
		activePointerId = event.pointerId;
		track.style.transition = 'none';
		stopAutoplay();
		container.setPointerCapture?.( event.pointerId );
	} );

	container.addEventListener( 'pointermove', ( event ) => {
		if ( pointerStartX === null || event.pointerId !== activePointerId ) {
			return;
		}
		const dx = event.clientX - pointerStartX;
		track.style.transform = `translate3d(${ pointerStartOffset + dx }px, 0, 0)`;
	} );

	const endPointer = ( event ) => {
		if ( pointerStartX === null || event.pointerId !== activePointerId ) {
			return;
		}
		const dx = event.clientX - pointerStartX;
		pointerStartX = null;
		activePointerId = null;
		const threshold = slideWidth() * SWIPE_THRESHOLD_RATIO;
		if ( dx < -threshold ) {
			go( 1 );
		} else if ( dx > threshold ) {
			go( -1 );
		} else {
			apply( true );
		}
		startAutoplay();
	};

	container.addEventListener( 'pointerup', endPointer );
	container.addEventListener( 'pointercancel', endPointer );

	track.querySelectorAll( 'a, img' ).forEach( ( el ) => {
		el.addEventListener( 'dragstart', ( event ) => event.preventDefault() );
	} );

	apply( false );
	startAutoplay();
};

document
	.querySelectorAll( '.js-ctaBlokkenSlider' )
	.forEach( initSlider );
